/**
 * Interactive Content Builder
 * Visual builder for creating surveys, polls, and quizzes
 */

import React, { useState, useEffect } from 'react';
import {
  Plus,
  Save,
  X,
  GripVertical,
  Pencil,
  Trash2,
  Copy,
  Eye,
  Link as LinkIcon,
  BarChart3,
  Globe,
  History,
  Send,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';

import useInteractiveContent from '@/hooks/useInteractiveContent';
import {
  InteractiveContent,
  Question,
  ContentType,
  ContentStatus,
  QuestionType,
  ContentSettings,
  CreateContentRequest,
} from '@/services/api/interactiveContent';

interface InteractiveContentBuilderProps {
  onClose?: () => void;
  editId?: number;
}

const CONTENT_TYPE_LABELS: Record<ContentType, string> = {
  survey: 'Survey',
  poll: 'Poll',
  quiz: 'Quiz',
};

const CONTENT_TYPE_DESCRIPTIONS: Record<ContentType, string> = {
  survey: 'Collect feedback and opinions with multiple question types',
  poll: 'Quick single-question vote with instant results',
  quiz: 'Test knowledge with scored questions and passing thresholds',
};

const QUESTION_TYPES: { value: QuestionType; label: string; icon: string; description: string }[] = [
  { value: 'single_choice', label: 'Single Choice', icon: '🔘', description: 'Select one option' },
  { value: 'multiple_choice', label: 'Multiple Choice', icon: '☑️', description: 'Select multiple options' },
  { value: 'text', label: 'Text Answer', icon: '📝', description: 'Free text response' },
  { value: 'rating', label: 'Rating', icon: '⭐', description: 'Star rating (1-5)' },
  { value: 'scale', label: 'Scale', icon: '📊', description: 'Numeric scale (e.g., 1-10)' },
  { value: 'boolean', label: 'Yes/No', icon: '✅', description: 'Simple yes or no' },
  { value: 'ranking', label: 'Ranking', icon: '📋', description: 'Order items by preference' },
];

const STATUS_COLORS: Record<ContentStatus, string> = {
  draft: 'bg-gray-100 text-gray-700',
  active: 'bg-green-100 text-green-700',
  paused: 'bg-yellow-100 text-yellow-700',
  archived: 'bg-red-100 text-red-700',
};

export const InteractiveContentBuilder: React.FC<InteractiveContentBuilderProps> = ({
  onClose,
  editId,
}) => {
  const {
    items,
    loading,
    error,
    createContent,
    updateContent,
    deleteContent,
    getContent,
    cloneContent,
    createLink,
    addToMediaLibrary,
    refetch,
  } = useInteractiveContent({ autoFetch: true });

  // View states
  const [view, setView] = useState<'list' | 'create' | 'edit'>('list');
  const [selectedContent, setSelectedContent] = useState<InteractiveContent | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [filterType, setFilterType] = useState<ContentType | 'all'>('all');
  const [filterStatus, setFilterStatus] = useState<ContentStatus | 'all'>('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Form state
  const [contentType, setContentType] = useState<ContentType>('survey');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [questions, setQuestions] = useState<Question[]>([]);
  const [settings, setSettings] = useState<ContentSettings>({
    allow_anonymous: false,
    require_all_questions: true,
    show_progress_bar: true,
    one_response_per_member: true,
    thank_you_message: 'Thank you for your response!',
  });

  // Question editor state
  const [showQuestionEditor, setShowQuestionEditor] = useState(false);
  const [editingQuestionIndex, setEditingQuestionIndex] = useState<number | null>(null);
  const [currentQuestion, setCurrentQuestion] = useState<Question>({
    id: '',
    type: 'single_choice',
    question: '',
    required: true,
    options: [''],
    order: 0,
  });

  // Link dialog state
  const [newLink, setNewLink] = useState({
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    custom_slug: '',
  });
  const [createdLink, setCreatedLink] = useState<string | null>(null);

  // Load content for editing
  useEffect(() => {
    if (editId) {
      loadContent(editId);
    }
  }, [editId]);

  const loadContent = async (id: number) => {
    const content = await getContent(id);
    if (content) {
      setSelectedContent(content);
      setContentType(content.content_type);
      setName(content.name);
      setDescription(content.description || '');
      setQuestions(content.questions);
      setSettings(content.settings);
      setView('edit');
    }
  };

  const resetForm = () => {
    setContentType('survey');
    setName('');
    setDescription('');
    setQuestions([]);
    setSettings({
      allow_anonymous: false,
      require_all_questions: true,
      show_progress_bar: true,
      one_response_per_member: true,
      thank_you_message: 'Thank you for your response!',
    });
    setSelectedContent(null);
    setView('list');
  };

  const handleAddQuestion = () => {
    setCurrentQuestion({
      id: `q_${Date.now()}`,
      type: 'single_choice',
      question: '',
      required: true,
      options: ['Option 1'],
      order: questions.length,
    });
    setEditingQuestionIndex(null);
    setShowQuestionEditor(true);
  };

  const handleEditQuestion = (index: number) => {
    setCurrentQuestion({ ...questions[index] });
    setEditingQuestionIndex(index);
    setShowQuestionEditor(true);
  };

  const handleSaveQuestion = () => {
    if (!currentQuestion.question.trim()) {
      alert('Please enter a question');
      return;
    }

    const updatedQuestions = [...questions];
    if (editingQuestionIndex !== null) {
      updatedQuestions[editingQuestionIndex] = currentQuestion;
    } else {
      updatedQuestions.push(currentQuestion);
    }
    setQuestions(updatedQuestions);
    setShowQuestionEditor(false);
  };

  const handleDeleteQuestion = (index: number) => {
    if (window.confirm('Delete this question?')) {
      setQuestions(questions.filter((_, i) => i !== index));
    }
  };

  const handleMoveQuestion = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= questions.length) return;

    const updatedQuestions = [...questions];
    [updatedQuestions[index], updatedQuestions[newIndex]] = [
      updatedQuestions[newIndex],
      updatedQuestions[index],
    ];
    // Update order
    updatedQuestions.forEach((q, i) => (q.order = i));
    setQuestions(updatedQuestions);
  };

  const handleAddOption = () => {
    setCurrentQuestion({
      ...currentQuestion,
      options: [...(currentQuestion.options || []), `Option ${(currentQuestion.options?.length || 0) + 1}`],
    });
  };

  const handleRemoveOption = (index: number) => {
    if ((currentQuestion.options?.length || 0) <= 1) return;
    setCurrentQuestion({
      ...currentQuestion,
      options: currentQuestion.options?.filter((_, i) => i !== index),
    });
  };

  const handleSaveContent = async () => {
    if (!name.trim()) {
      alert('Please enter a name');
      return;
    }
    if (questions.length === 0) {
      alert('Please add at least one question');
      return;
    }

    const data: CreateContentRequest = {
      content_type: contentType,
      name,
      description: description || undefined,
      questions,
      settings,
    };

    let result;
    if (selectedContent) {
      result = await updateContent(selectedContent.id, data);
    } else {
      result = await createContent(data);
    }

    if (result) {
      alert(selectedContent ? 'Content updated successfully' : 'Content created successfully');
      resetForm();
      refetch();
    }
  };

  const handlePublish = async () => {
    if (!selectedContent) return;

    const result = await updateContent(selectedContent.id, { status: 'active' });
    if (result) {
      alert('Content published successfully!');
      refetch();
      loadContent(selectedContent.id);
    }
  };

  const handleClone = async (content: InteractiveContent) => {
    const cloned = await cloneContent(content.id, `${content.name} (Copy)`);
    if (cloned) {
      alert('Content cloned successfully');
      refetch();
    }
  };

  const handleDelete = async (content: InteractiveContent) => {
    if (window.confirm(`Delete "${content.name}"? This cannot be undone.`)) {
      const success = await deleteContent(content.id);
      if (success) {
        alert('Content deleted');
        refetch();
      }
    }
  };

  const handleCreateLink = async () => {
    if (!selectedContent) return;

    const link = await createLink(selectedContent.id, {
      utm_source: newLink.utm_source || undefined,
      utm_medium: newLink.utm_medium || undefined,
      utm_campaign: newLink.utm_campaign || undefined,
      custom_slug: newLink.custom_slug || undefined,
    });

    if (link) {
      setCreatedLink(link.full_url);
    }
  };

  const handleAddToMediaLibrary = async () => {
    if (!selectedContent) return;

    const result = await addToMediaLibrary(selectedContent.id);
    if (result) {
      alert(`Added to media library! Short URL: ${result.short_url}`);
    }
  };

  // Filter items
  const filteredItems = items.filter((item) => {
    if (filterType !== 'all' && item.content_type !== filterType) return false;
    if (filterStatus !== 'all' && item.status !== filterStatus) return false;
    if (searchTerm && !item.name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  // Render list view
  const renderListView = () => (
    <div className="space-y-4">
      {/* Toolbar (page title rendered by the shell chrome) */}
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button onClick={() => setView('create')}>
          <Plus className="w-4 h-4 mr-2" />
          Create New
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-center">
        <Input
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-64"
        />
        <Select value={filterType} onValueChange={(v) => setFilterType(v as ContentType | 'all')}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="survey">Surveys</SelectItem>
            <SelectItem value="poll">Polls</SelectItem>
            <SelectItem value="quiz">Quizzes</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={(v) => setFilterStatus(v as ContentStatus | 'all')}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="paused">Paused</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Content list */}
      <div className="grid gap-4">
        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading...</div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No content found. Create your first survey, poll, or quiz!
          </div>
        ) : (
          filteredItems.map((item) => (
            <Card key={item.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center gap-2">
                      <CardTitle className="text-lg">{item.name}</CardTitle>
                      <Badge className={STATUS_COLORS[item.status]}>{item.status}</Badge>
                      <Badge variant="outline">{CONTENT_TYPE_LABELS[item.content_type]}</Badge>
                    </div>
                    {item.description && (
                      <CardDescription className="mt-1">{item.description}</CardDescription>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => loadContent(item.id)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleClone(item)}>
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(item)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex gap-6 text-sm text-gray-500">
                  <span>{item.question_count || 0} questions</span>
                  <span>{item.response_count || 0} responses</span>
                  <span>{item.link_count || 0} links</span>
                  <span>v{item.version}</span>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );

  // Render create/edit view
  const renderEditorView = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={resetForm}>
            <X className="w-4 h-4 mr-2" />
            Cancel
          </Button>
          <h2 className="text-xl font-semibold">
            {selectedContent ? `Edit: ${selectedContent.name}` : 'Create New Content'}
          </h2>
          {selectedContent && (
            <Badge className={STATUS_COLORS[selectedContent.status]}>{selectedContent.status}</Badge>
          )}
        </div>
        <div className="flex gap-2">
          {selectedContent && selectedContent.status === 'draft' && (
            <Button variant="outline" onClick={handlePublish}>
              <Send className="w-4 h-4 mr-2" />
              Publish
            </Button>
          )}
          {selectedContent && (
            <>
              <Button variant="outline" onClick={() => setShowLinkDialog(true)}>
                <LinkIcon className="w-4 h-4 mr-2" />
                Create Link
              </Button>
              <Button variant="outline" onClick={handleAddToMediaLibrary}>
                <Globe className="w-4 h-4 mr-2" />
                Add to Media Library
              </Button>
            </>
          )}
          <Button onClick={handleSaveContent} disabled={loading}>
            <Save className="w-4 h-4 mr-2" />
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="content" className="space-y-4">
        <TabsList>
          <TabsTrigger value="content">Content</TabsTrigger>
          <TabsTrigger value="questions">Questions ({questions.length})</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
          {selectedContent && <TabsTrigger value="analytics">Analytics</TabsTrigger>}
        </TabsList>

        <TabsContent value="content" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {!selectedContent && (
                <div className="space-y-2">
                  <Label>Content Type</Label>
                  <div className="grid grid-cols-3 gap-4">
                    {(['survey', 'poll', 'quiz'] as ContentType[]).map((type) => (
                      <Card
                        key={type}
                        className={`cursor-pointer transition-all ${
                          contentType === type ? 'border-primary ring-2 ring-primary' : ''
                        }`}
                        onClick={() => setContentType(type)}
                      >
                        <CardContent className="p-4">
                          <div className="font-medium">{CONTENT_TYPE_LABELS[type]}</div>
                          <div className="text-sm text-gray-500">{CONTENT_TYPE_DESCRIPTIONS[type]}</div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter a name for your content"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Optional description"
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="questions" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Questions</CardTitle>
              <Button onClick={handleAddQuestion}>
                <Plus className="w-4 h-4 mr-2" />
                Add Question
              </Button>
            </CardHeader>
            <CardContent>
              {questions.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No questions yet. Add your first question to get started.
                </div>
              ) : (
                <div className="space-y-3">
                  {questions.map((q, index) => (
                    <div
                      key={q.id}
                      className="flex items-start gap-3 p-4 border rounded-lg bg-gray-50"
                    >
                      <div className="flex flex-col gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleMoveQuestion(index, 'up')}
                          disabled={index === 0}
                        >
                          ↑
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleMoveQuestion(index, 'down')}
                          disabled={index === questions.length - 1}
                        >
                          ↓
                        </Button>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium">Q{index + 1}.</span>
                          <Badge variant="outline">
                            {QUESTION_TYPES.find((t) => t.value === q.type)?.label || q.type}
                          </Badge>
                          {q.required && <Badge variant="secondary">Required</Badge>}
                          {contentType === 'quiz' && q.points && (
                            <Badge variant="outline">{q.points} pts</Badge>
                          )}
                        </div>
                        <div className="text-gray-700">{q.question}</div>
                        {q.options && q.options.length > 0 && (
                          <div className="mt-2 text-sm text-gray-500">
                            Options: {q.options.join(', ')}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEditQuestion(index)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteQuestion(index)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Response Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Allow Anonymous Responses</Label>
                  <p className="text-sm text-gray-500">Allow responses without identifying the respondent</p>
                </div>
                <Switch
                  checked={settings.allow_anonymous}
                  onCheckedChange={(checked) => setSettings({ ...settings, allow_anonymous: checked })}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Require All Questions</Label>
                  <p className="text-sm text-gray-500">All questions must be answered to submit</p>
                </div>
                <Switch
                  checked={settings.require_all_questions}
                  onCheckedChange={(checked) => setSettings({ ...settings, require_all_questions: checked })}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Show Progress Bar</Label>
                  <p className="text-sm text-gray-500">Display completion progress to respondents</p>
                </div>
                <Switch
                  checked={settings.show_progress_bar}
                  onCheckedChange={(checked) => setSettings({ ...settings, show_progress_bar: checked })}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>One Response Per Member</Label>
                  <p className="text-sm text-gray-500">Prevent duplicate submissions from same member</p>
                </div>
                <Switch
                  checked={settings.one_response_per_member}
                  onCheckedChange={(checked) =>
                    setSettings({ ...settings, one_response_per_member: checked })
                  }
                />
              </div>

              {(contentType === 'poll' || contentType === 'quiz') && (
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Show Results After Submit</Label>
                    <p className="text-sm text-gray-500">
                      {contentType === 'quiz' ? 'Show correct answers' : 'Show current poll results'}
                    </p>
                  </div>
                  <Switch
                    checked={settings.show_results_after_submit}
                    onCheckedChange={(checked) =>
                      setSettings({ ...settings, show_results_after_submit: checked })
                    }
                  />
                </div>
              )}

              {contentType === 'quiz' && (
                <div className="space-y-2">
                  <Label>Passing Score (%)</Label>
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    value={settings.passing_score || ''}
                    onChange={(e) =>
                      setSettings({ ...settings, passing_score: parseInt(e.target.value) || undefined })
                    }
                    placeholder="70"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label>Thank You Message</Label>
                <Textarea
                  value={settings.thank_you_message || ''}
                  onChange={(e) => setSettings({ ...settings, thank_you_message: e.target.value })}
                  placeholder="Thank you for your response!"
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {selectedContent && (
          <TabsContent value="analytics" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Response Analytics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold">{selectedContent.stats?.total_responses || 0}</div>
                    <div className="text-sm text-gray-500">Total Responses</div>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold">{selectedContent.stats?.completed_responses || 0}</div>
                    <div className="text-sm text-gray-500">Completed</div>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold">
                      {selectedContent.stats?.avg_time_seconds
                        ? `${Math.round(selectedContent.stats.avg_time_seconds / 60)}m`
                        : '-'}
                    </div>
                    <div className="text-sm text-gray-500">Avg Time</div>
                  </div>
                  {contentType === 'quiz' && (
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <div className="text-2xl font-bold">
                        {selectedContent.stats?.avg_score ? `${selectedContent.stats.avg_score}%` : '-'}
                      </div>
                      <div className="text-sm text-gray-500">Avg Score</div>
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t">
                  <h4 className="font-medium mb-2">Sharing Links ({selectedContent.links?.length || 0})</h4>
                  {selectedContent.links && selectedContent.links.length > 0 ? (
                    <div className="space-y-2">
                      {selectedContent.links.map((link) => (
                        <div key={link.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <div>
                            <code className="text-sm">{link.full_url}</code>
                            {link.utm_campaign && (
                              <span className="ml-2 text-xs text-gray-500">({link.utm_campaign})</span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">{link.click_count || 0} clicks</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No sharing links created yet.</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Question Editor Dialog */}
      <Dialog open={showQuestionEditor} onOpenChange={setShowQuestionEditor}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingQuestionIndex !== null ? 'Edit Question' : 'Add Question'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Question Type</Label>
              <Select
                value={currentQuestion.type}
                onValueChange={(v) => setCurrentQuestion({ ...currentQuestion, type: v as QuestionType })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {QUESTION_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      <span className="flex items-center gap-2">
                        <span>{type.icon}</span>
                        <span>{type.label}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Question *</Label>
              <Textarea
                value={currentQuestion.question}
                onChange={(e) => setCurrentQuestion({ ...currentQuestion, question: e.target.value })}
                placeholder="Enter your question"
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Input
                value={currentQuestion.description || ''}
                onChange={(e) => setCurrentQuestion({ ...currentQuestion, description: e.target.value })}
                placeholder="Additional help text for respondents"
              />
            </div>

            {['single_choice', 'multiple_choice', 'ranking'].includes(currentQuestion.type) && (
              <div className="space-y-2">
                <Label>Options</Label>
                <div className="space-y-2">
                  {currentQuestion.options?.map((option, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        value={option}
                        onChange={(e) => {
                          const newOptions = [...(currentQuestion.options || [])];
                          newOptions[index] = e.target.value;
                          setCurrentQuestion({ ...currentQuestion, options: newOptions });
                        }}
                        placeholder={`Option ${index + 1}`}
                      />
                      {contentType === 'quiz' && (
                        <Button
                          variant={currentQuestion.correct_answer === option ? 'default' : 'outline'}
                          size="sm"
                          onClick={() =>
                            setCurrentQuestion({ ...currentQuestion, correct_answer: option })
                          }
                          title="Mark as correct answer"
                        >
                          {currentQuestion.correct_answer === option ? (
                            <CheckCircle className="w-4 h-4" />
                          ) : (
                            <XCircle className="w-4 h-4" />
                          )}
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveOption(index)}
                        disabled={(currentQuestion.options?.length || 0) <= 1}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={handleAddOption}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Option
                  </Button>
                </div>
              </div>
            )}

            {currentQuestion.type === 'scale' && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Min Value</Label>
                  <Input
                    type="number"
                    value={currentQuestion.min_value || 1}
                    onChange={(e) =>
                      setCurrentQuestion({ ...currentQuestion, min_value: parseInt(e.target.value) })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Max Value</Label>
                  <Input
                    type="number"
                    value={currentQuestion.max_value || 10}
                    onChange={(e) =>
                      setCurrentQuestion({ ...currentQuestion, max_value: parseInt(e.target.value) })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Min Label</Label>
                  <Input
                    value={currentQuestion.min_label || ''}
                    onChange={(e) => setCurrentQuestion({ ...currentQuestion, min_label: e.target.value })}
                    placeholder="e.g., Not at all"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Max Label</Label>
                  <Input
                    value={currentQuestion.max_label || ''}
                    onChange={(e) => setCurrentQuestion({ ...currentQuestion, max_label: e.target.value })}
                    placeholder="e.g., Extremely"
                  />
                </div>
              </div>
            )}

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="required"
                  checked={currentQuestion.required}
                  onCheckedChange={(checked) =>
                    setCurrentQuestion({ ...currentQuestion, required: checked as boolean })
                  }
                />
                <Label htmlFor="required">Required</Label>
              </div>

              {contentType === 'quiz' && (
                <div className="flex items-center gap-2">
                  <Label>Points:</Label>
                  <Input
                    type="number"
                    min={0}
                    value={currentQuestion.points || ''}
                    onChange={(e) =>
                      setCurrentQuestion({ ...currentQuestion, points: parseInt(e.target.value) || undefined })
                    }
                    placeholder="10"
                    className="w-20"
                  />
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQuestionEditor(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveQuestion}>Save Question</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Link Dialog */}
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Sharing Link</DialogTitle>
          </DialogHeader>

          {createdLink ? (
            <div className="space-y-4">
              <Alert>
                <AlertDescription>
                  <div className="space-y-2">
                    <p className="font-medium">Link created successfully!</p>
                    <code className="block p-2 bg-gray-100 rounded text-sm break-all">{createdLink}</code>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText(createdLink);
                        alert('Copied to clipboard!');
                      }}
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Copy Link
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>
              <Button
                onClick={() => {
                  setCreatedLink(null);
                  setShowLinkDialog(false);
                  setNewLink({ utm_source: '', utm_medium: '', utm_campaign: '', custom_slug: '' });
                }}
              >
                Close
              </Button>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Custom Slug (optional)</Label>
                  <Input
                    value={newLink.custom_slug}
                    onChange={(e) => setNewLink({ ...newLink, custom_slug: e.target.value })}
                    placeholder="e.g., my-survey-2024"
                  />
                </div>

                <div className="space-y-2">
                  <Label>UTM Source</Label>
                  <Input
                    value={newLink.utm_source}
                    onChange={(e) => setNewLink({ ...newLink, utm_source: e.target.value })}
                    placeholder="e.g., email, sms, social"
                  />
                </div>

                <div className="space-y-2">
                  <Label>UTM Medium</Label>
                  <Input
                    value={newLink.utm_medium}
                    onChange={(e) => setNewLink({ ...newLink, utm_medium: e.target.value })}
                    placeholder="e.g., newsletter, campaign"
                  />
                </div>

                <div className="space-y-2">
                  <Label>UTM Campaign</Label>
                  <Input
                    value={newLink.utm_campaign}
                    onChange={(e) => setNewLink({ ...newLink, utm_campaign: e.target.value })}
                    placeholder="e.g., q4-feedback"
                  />
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setShowLinkDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreateLink}>Create Link</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );

  return (
    <div className="p-6">
      {view === 'list' && renderListView()}
      {(view === 'create' || view === 'edit') && renderEditorView()}
    </div>
  );
};

export default InteractiveContentBuilder;
